// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * D-SUPPORT-4 team management: the Team page. Supervisors see everything and edit
 * COVERAGE; managers additionally edit profiles/hours/role, force sign-out, and
 * deactivate. All member endpoints are server-side restricted to support-tier
 * targets — this surface can never touch an admin or a pharmacy user.
 */
import { useEffect, useState } from 'react'
import { Users2 } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { useAuth } from '../../auth/AuthContext'
import { ErrorBanner, LoadingState, EmptyState } from '../../components/workflow/StatePills'
import SearchableSelect from '../../components/common/SearchableSelect'
import SupportInviteBar from './SupportInviteBar'
import DeliveryPill from '../../components/workflow/DeliveryPill'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

// The role as RolePill prints it.
const roleText = (role: string | null) =>
  role === 'support_manager' ? 'Manager' : role === 'support_supervisor' ? 'Supervisor' : 'Agent'

// D-UI-2: what each column shows, for its header funnel. Keys are the lower-cased header labels.
const MEMBER_FUNNELS: FunnelColumn<any>[] = [
  { key: 'member',   label: 'Member',   value: m => m.display_name ?? m.email },
  { key: 'role',     label: 'Role',     value: m => roleText(m.role) },
  { key: 'status',   label: 'Status',   value: m => m.deactivated ? 'disabled' : m.status },
  { key: 'sessions', label: 'Sessions', value: m => m.active_sessions },
  { key: 'open',     label: 'Open',     value: m => m.open_tickets },
  { key: 'coverage', label: 'Coverage', value: m => m.coverage_count },
  { key: 'hours',    label: 'Hours',    value: m => m.work_start ? `${m.work_start}–${m.work_end ?? '?'}` : null },
]
const INVITE_FUNNELS: FunnelColumn<api.SupportInvitation>[] = [
  { key: 'person',         label: 'Person',         value: i => i.display_name ?? i.email },
  { key: 'role',           label: 'Role',           value: i => roleText(i.role) },
  { key: 'invited',        label: 'Invited',        value: i => i.created_at },
  // As printed: an expired invitation shows the word, so the date is given as the cell shows it.
  { key: 'expires',        label: 'Expires',        value: i => i.status === 'expired' ? 'expired' : new Date(i.expires_at).toLocaleDateString('en-US') },
  { key: 'email delivery', label: 'Email delivery', value: i => i.email_status ?? 'never queued' },
]

export default function SupportTeamMembersPage() {
  const { hasPermission } = useAuth()
  const isManager = hasPermission('support.settings.manage')
  const [members, setMembers] = useState<any[]>([])
  const [invites, setInvites] = useState<api.SupportInvitation[]>([])
  const [customers, setCustomers] = useState<api.SupportCustomer[]>([])
  const [sel, setSel] = useState<string | null>(null)
  const [detail, setDetail] = useState<any | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // profile draft
  const [dn, setDn] = useState(''); const [title, setTitle] = useState(''); const [phone, setPhone] = useState('')
  const [ws, setWs] = useState(''); const [we, setWe] = useState(''); const [wd, setWd] = useState('')
  const [role, setRole] = useState('support')
  const [cov, setCov] = useState<Set<string>>(new Set())

  const loadList = () => {
    setLoading(true)
    Promise.all([api.listSupportMembers(), api.listSupportCustomers()])
      .then(([m, c]) => { setMembers(m.members); setCustomers(c.customers) })
      .catch(e => setError(friendlyError(e)))
      .finally(() => setLoading(false))
    // Loaded separately and deliberately non-fatally: a failure here must not blank the
    // members table, but it must not be silent either — the panel says so itself.
    api.listSupportInvitations()
      .then(r => { setInvites(r.invitations); setInviteError(null) })
      .catch(e => setInviteError(friendlyError(e)))
  }
  useEffect(loadList, [])

  const open = (id: string) => {
    setSel(id); setDetail(null)
    api.getSupportMember(id).then(d => {
      setDetail(d)
      setDn(d.profile?.display_name ?? ''); setTitle(d.profile?.title ?? ''); setPhone(d.profile?.phone ?? '')
      setWs(d.profile?.work_start ?? ''); setWe(d.profile?.work_end ?? ''); setWd(d.profile?.work_days ?? '')
      setRole(members.find(m => m.id === id)?.role ?? 'support')
      setCov(new Set((d.coverage ?? []).map((c: any) => c.pharmacy_id)))
    }).catch(e => setError(friendlyError(e)))
  }

  const act = async (fn: () => Promise<any>, reloadDetail = true) => {
    setBusy(true); setError(null)
    try { await fn(); loadList(); if (reloadDetail && sel) open(sel) }
    catch (e) { setError(friendlyError(e)) }
    finally { setBusy(false) }
  }

  const selMember = members.find(m => m.id === sel)

  // The header funnels filter the whole loaded list.
  const fx = useTableFunnels(members, MEMBER_FUNNELS)

  return (
    <div style={{ padding: '24px 24px 48px', maxWidth: 'var(--page-max, 1280px)', margin: '0 auto', width: '100%' }}>
      <header style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><Users2 size={21} /> Team</h1>
        <div style={{ color: 'var(--color-text-mute)', fontSize: 13, marginTop: 4 }}>
          Profiles, sessions, coverage, and working hours for the support team.
        </div>
      </header>

      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

      <SupportInviteBar onInvited={loadList} />

      <PendingInvitations invites={invites} error={inviteError} />

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Members table */}
        <div style={{ ...card, flex: '1 1 460px', minWidth: 420 }}>
          {loading ? <LoadingState label="Loading team…" /> :
           members.length === 0 ? <EmptyState message="No support members yet — invite from Support Home." /> :
          <>
          {fx.chips}
          <table {...fx.tableProps} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead><tr>{['Member', 'Role', 'Status', 'Sessions', 'Open', 'Coverage', 'Hours'].map(h => <th key={h} style={th}>{h} {fx.funnel(h.toLowerCase())}</th>)}</tr></thead>
            <tbody>
              {fx.rows.map(m => (
                <tr key={m.id} onClick={() => open(m.id)}
                    style={{ borderTop: '1px solid var(--color-border)', cursor: 'pointer', background: sel === m.id ? 'var(--color-bg)' : 'transparent' }}>
                  <td style={{ ...td, fontWeight: 600 }}>{m.display_name ?? m.email}<div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', fontWeight: 400 }}>{m.title ?? m.email}</div></td>
                  <td style={td}><RolePill role={m.role} /></td>
                  <td style={td}>{m.deactivated ? <span style={{ color: '#b91c1c', fontWeight: 600 }}>disabled</span> : m.status}</td>
                  <td style={td}>{m.active_sessions}</td>
                  <td style={td}>{m.open_tickets}</td>
                  <td style={td}>{m.coverage_count}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{m.work_start ? `${m.work_start}–${m.work_end ?? '?'}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </>}
        </div>

        {/* Detail panel */}
        {sel && (
          <div style={{ ...card, flex: '1 1 380px', minWidth: 360 }}>
            {!detail ? <LoadingState label="Loading member…" /> : <>
              <h2 style={h2}>{detail.profile?.display_name ?? detail.profile?.email}</h2>
              <div style={{ fontSize: 12.5, color: 'var(--color-text-mute)', marginBottom: 12 }}>
                {detail.profile?.email} · last login {detail.profile?.last_login_at ? new Date(detail.profile.last_login_at).toLocaleString() : 'never'}
              </div>

              {isManager && <>
                <h3 style={h3}>Profile</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                  <input value={dn} onChange={e => setDn(e.target.value)} placeholder="Display name" style={inp} />
                  <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title" style={inp} />
                  <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone" style={inp} />
                  <SearchableSelect value={role} onChange={setRole} ariaLabel="Role"
                    options={[{ value: 'support', label: 'Agent' }, { value: 'support_supervisor', label: 'Supervisor' }, { value: 'support_manager', label: 'Manager' }]}
                    style={{ fontSize: 13 }} />
                </div>
                <h3 style={h3}>Working hours</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
                  <input type="time" value={ws} onChange={e => setWs(e.target.value)} aria-label="Work start" style={inp} />
                  <input type="time" value={we} onChange={e => setWe(e.target.value)} aria-label="Work end" style={inp} />
                  <input value={wd} onChange={e => setWd(e.target.value)} placeholder="Mon-Fri" style={inp} />
                </div>
                <button disabled={busy} style={btnPrimary}
                  onClick={() => act(async () => {
                    await api.putSupportMemberProfile(sel!, { display_name: dn, title, phone, work_start: ws, work_end: we, work_days: wd })
                    if (role !== selMember?.role) await api.putSupportMemberRole(sel!, role)
                  })}>
                  {busy ? 'Saving…' : 'Save profile'}
                </button>
              </>}

              <h3 style={h3}>Customer coverage</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8, maxHeight: 160, overflowY: 'auto' }}>
                {customers.map(c => (
                  <label key={c.id} style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={cov.has(c.id)}
                      onChange={e => setCov(prev => { const n = new Set(prev); e.target.checked ? n.add(c.id) : n.delete(c.id); return n })} />
                    {c.name}
                  </label>
                ))}
              </div>
              <button disabled={busy} style={btnGhost}
                onClick={() => act(() => api.putSupportMemberCoverage(sel!, [...cov]))}>
                Save coverage ({cov.size})
              </button>
              {detail.ownership?.length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginTop: 6 }}>
                  Owns: {detail.ownership.map((o: any) => `${o.name} (${o.slot})`).join(', ')}
                </div>
              )}

              <h3 style={h3}>Active sessions ({detail.sessions?.length ?? 0})</h3>
              {(detail.sessions ?? []).slice(0, 5).map((s: any) => (
                <div key={s.id} style={{ fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--color-border)' }}>
                  {s.ip ?? 'ip?'} · {new Date(s.last_seen_at ?? s.created_at).toLocaleString()}
                  <span style={{ color: 'var(--color-text-mute)' }}> · {s.user_agent?.slice(0, 40)}</span>
                </div>
              ))}
              {isManager && (detail.sessions?.length ?? 0) > 0 && (
                <button disabled={busy} style={{ ...btnGhost, marginTop: 6, color: '#b91c1c' }}
                  onClick={() => act(() => api.signOutSupportMember(sel!))}>
                  Force sign-out everywhere
                </button>
              )}

              <h3 style={h3}>Recent activity</h3>
              <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                {(detail.audit ?? []).map((a: any, i: number) => (
                  <div key={i} style={{ fontSize: 12, padding: '3px 0', borderBottom: '1px solid var(--color-border)' }}>
                    <b>{a.action}</b> {a.entity_type ?? ''} <span style={{ color: 'var(--color-text-mute)' }}>{new Date(a.occurred_at).toLocaleString()}</span>
                  </div>
                ))}
                {(detail.audit ?? []).length === 0 && <div style={{ fontSize: 12.5, color: 'var(--color-text-mute)' }}>No recorded activity.</div>}
              </div>

              {isManager && (
                <button disabled={busy}
                  style={{ ...btnGhost, marginTop: 14, color: detail.profile?.deactivated ? '#15803d' : '#b91c1c' }}
                  onClick={() => act(() => api.setSupportMemberActive(sel!, !!detail.profile?.deactivated))}>
                  {detail.profile?.deactivated ? 'Reactivate account' : 'Deactivate account'}
                </button>
              )}
            </>}
          </div>
        )}
      </div>
    </div>
  )
}


/**
 * D-SUPPORT-10: the answer to "did he actually get the invite?", in the place you would
 * look for it. Before this, an invitation existed only in the database — the members table
 * showed accepted accounts only, so an invited person was invisible the moment the
 * confirmation toast went away.
 *
 * Mail state is reported separately from invitation state on purpose. "We created an
 * invitation" and "the mail left the building" are different facts, and the failure Patrick
 * hit was not being able to distinguish them.
 */
function PendingInvitations({ invites, error }: { invites: api.SupportInvitation[]; error: string | null }) {
  const fx = useTableFunnels(invites, INVITE_FUNNELS)
  if (error) {
    return (
      <div style={{ ...card, marginBottom: 14 }}>
        <div style={h2}>Pending invitations</div>
        <div style={{ fontSize: 13, color: '#b91c1c', marginTop: 6 }}>
          Could not load invitations — {error}. That means this panel cannot confirm delivery right now;
          check SaaS Admin → Invitations instead.
        </div>
      </div>
    )
  }
  if (invites.length === 0) return null

  return (
    <div style={{ ...card, marginBottom: 14 }}>
      <div style={h2}>Pending invitations <span style={{ fontWeight: 500, color: 'var(--color-text-mute)', fontSize: 13 }}>({invites.length})</span></div>
      <div style={{ fontSize: 12.5, color: 'var(--color-text-mute)', marginBottom: 8 }}>
        People invited but not yet signed in. They do not appear in the members list until they accept.
      </div>
      {fx.chips}
      <div style={{ overflowX: 'auto' }}>
        <table {...fx.tableProps} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr>{['Person', 'Role', 'Invited', 'Expires', 'Email delivery'].map(x => <th key={x} style={th}>{x} {fx.funnel(x.toLowerCase())}</th>)}</tr></thead>
          <tbody>
            {fx.rows.map(i => (
              <tr key={i.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{i.display_name ?? i.email}</div>
                  {i.display_name && <div style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>{i.email}</div>}
                  {i.invited_by && <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)' }}>invited by {i.invited_by}</div>}
                </td>
                <td style={td}><RolePill role={i.role} /></td>
                <td style={td}>{new Date(i.created_at).toLocaleDateString('en-US')}</td>
                <td style={td}>
                  {i.status === 'expired'
                    ? <span style={{ color: '#b91c1c', fontWeight: 600 }}>expired</span>
                    : new Date(i.expires_at).toLocaleDateString('en-US')}
                </td>
                <td style={td}><DeliveryPill status={i.email_status} sentAt={i.email_sent_at} error={i.email_error} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}


function RolePill({ role }: { role: string | null }) {
  const label = role === 'support_manager' ? 'Manager' : role === 'support_supervisor' ? 'Supervisor' : 'Agent'
  const bg = role === 'support_manager' ? '#e0e7ff' : role === 'support_supervisor' ? '#fef3c7' : '#dcfce7'
  const fg = role === 'support_manager' ? '#3730a3' : role === 'support_supervisor' ? '#b45309' : '#15803d'
  return <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: bg, color: fg }}>{label}</span>
}

const card: React.CSSProperties = { background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 14 }
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 8px', color: 'var(--color-text-mute)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.4px' }
const td: React.CSSProperties = { padding: '9px 8px' }
const h2: React.CSSProperties = { fontSize: 17, fontWeight: 700, margin: '0 0 2px' }
const h3: React.CSSProperties = { fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: 'var(--color-text-mute)', margin: '14px 0 6px' }
const inp: React.CSSProperties = { padding: '7px 9px', borderRadius: 7, border: '1px solid var(--color-border)', fontSize: 13, background: 'var(--color-bg)', color: 'var(--color-text)' }
const btnPrimary: React.CSSProperties = { border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: 'var(--color-primary, #0f766e)', color: '#fff' }
const btnGhost: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 8, padding: '7px 12px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', background: 'var(--color-bg)', color: 'var(--color-text)' }
